import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAIRecipeDetail, SimplifiedRecipeSuggestion } from 'hooks/useAIRecipeDetail.ts';
import './RecipeDetail.css';
import ReasoningPanel from './components/ReasoningPanel.tsx';

const AIRecipeDetailPage: React.FC = () => {
  const navigate = useNavigate();
  type SuggestionState = { suggestion?: { title: string; description: string; estimated_minutes?: number; servings?: number; ingredients_description?: string } };
  const location = useLocation() as { state?: SuggestionState };
  const suggestion = location.state?.suggestion as SimplifiedRecipeSuggestion | undefined;
  // Pantry is now derived inside the hook from stockItemsMap; no need to compute here
  const { result, loading, streaming, error, requestStreaming, cancel, reasoningText } = useAIRecipeDetail({ suggestion: suggestion as SimplifiedRecipeSuggestion });

  useEffect(() => {
    if (!suggestion) {
      navigate('/recipes', { replace: true });
      return;
    }
    if (!result) void requestStreaming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!suggestion) return null;

  return (
    <div className="recipe-page">
      <header className="recipe-detail__header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <button type="button" className="recipe-detail__back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={requestStreaming}
              disabled={loading || streaming}
              aria-label="Refresh"
              title={loading || streaming ? 'Refreshing…' : 'Refresh'}
              style={{ background: 'transparent', border: 'none', padding: '4px 6px', fontSize: 16, color: '#666', cursor: 'pointer', borderRadius: 4, opacity: (loading || streaming) ? 0.5 : 0.85 }}
            >
              ↻
            </button>
            {(loading || streaming) && (
              <button
                type="button"
                onClick={cancel}
                aria-label="Cancel"
                title="Cancel"
                style={{ background: 'transparent', border: 'none', padding: '4px 6px', fontSize: 16, color: '#666', cursor: 'pointer', borderRadius: 4, opacity: 0.85 }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <h1 className="recipe-detail__title" style={{ marginTop: 8 }}>{suggestion?.title ?? 'Recipe'}</h1>
      </header>

      <main className="recipe-detail__main">
        {error && (
          <section>
            <p style={{ color: '#a00' }}>{error}</p>
          </section>
        )}
        {(streaming || reasoningText) && (
          <section>
            <ReasoningPanel reasoningText={reasoningText} streaming={streaming} initiallyOpen={false} />
          </section>
        )}
        <section>
          <h2 style={{ marginTop: 0 }}>Ingredients</h2>
          {!result && (loading || streaming) && <div>Loading ingredients…</div>}
          {result && (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {result.ingredients.map((ing, idx) => (
                <li key={idx}>
                  {ing.amount} {ing.unit} {ing.name}{ing.notes ? ` — ${ing.notes}` : ''}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 style={{ marginTop: 0 }}>Directions</h2>
          {!result && (loading || streaming) && <div>Loading directions…</div>}
          {result && (
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              {result.steps.map((s) => (
                <li key={s.number}>
                  <div>{s.instruction}</div>
                  {s.concurrency_note && (
                    <div style={{ opacity: 0.8 }}><em>{s.concurrency_note}</em></div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
};

export default AIRecipeDetailPage;


